import type { Money } from '@/lib/format';

/**
 * Hand-mirrored from the backend's zod schemas, the same way the customer app
 * and the Flutter rider app do it. When the backend grows an OpenAPI document,
 * generate this instead of maintaining it.
 *
 * Only the fields this app actually draws are modelled. A rider app that
 * deserialises a pharmacy's payable, or the order's line items, is one leak
 * away from showing them — and a rider carries a sealed bag, not a basket.
 */

// ── session ─────────────────────────────────────────────────────────────────

export type AuthUser = {
  id: string;
  phone: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  roles: string[];
};

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  isNewUser?: boolean;
};

/** The name to greet someone by, falling back to the number they signed in on. */
export function displayName(user: AuthUser | null | undefined): string {
  if (!user) return 'Rider';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name.length > 0 ? name : user.phone;
}

/**
 * Whether the platform has granted this person the rider role.
 *
 * Checked separately from having a rider PROFILE: the two are granted in two
 * steps in the admin panel — the role, then the `riders` row with a vehicle on
 * it — and someone caught between them needs to be told which half is missing
 * rather than shown an empty run sheet.
 */
export function hasRiderRole(user: AuthUser | null | undefined): boolean {
  return user?.roles.includes('RIDER') === true;
}

// ── the rider ───────────────────────────────────────────────────────────────

/**
 * Where a rider is in their shift.
 *
 * `BUSY` is not a state this app ever sets — the backend moves a rider into it
 * on acceptance and back to `AVAILABLE` when the job ends, so the shift switch
 * has two positions and the third appears underneath it. `SUSPENDED` is the
 * platform's to set and the platform's to lift; the app can only report it.
 */
export type RiderStatus = 'OFFLINE' | 'AVAILABLE' | 'BUSY' | 'SUSPENDED';

export type VehicleType = 'BICYCLE' | 'MOTORCYCLE' | 'SCOOTER' | 'CAR';

export type RiderProfile = {
  /**
   * The id every rider-scoped route is addressed by — going on shift, pushing
   * a ping. Not the same as the user id, which is why the app has to fetch
   * this before it can do either.
   */
  id: string;
  userId: string;
  status: RiderStatus;
  /** Set false by the platform when someone leaves. Distinct from SUSPENDED. */
  isActive: boolean;
  name?: string | null;
  phone?: string | null;
  vehicleType?: VehicleType | null;
  vehicleNumber?: string | null;
  supportsColdChain: boolean;
  /**
   * Jobs not yet in a terminal state, counted by the server. Shown so the
   * shift switch can explain why going offline will not empty the run sheet.
   */
  activeDeliveries: number;
};

export function isOnShift(rider: RiderProfile | null | undefined): boolean {
  return rider?.status === 'AVAILABLE' || rider?.status === 'BUSY';
}

/** Blocked from working, for either of the two quite different reasons. */
export function isBlocked(rider: RiderProfile | null | undefined): boolean {
  if (!rider) return false;
  return !rider.isActive || rider.status === 'SUSPENDED';
}

const VEHICLE_LABELS: Record<VehicleType, string> = {
  BICYCLE: 'Bicycle',
  MOTORCYCLE: 'Motorcycle',
  SCOOTER: 'Scooter',
  CAR: 'Car',
};

export function vehicleLabel(rider: RiderProfile | null | undefined): string {
  if (!rider) return '';
  const type = rider.vehicleType ? VEHICLE_LABELS[rider.vehicleType] : null;
  return [type, rider.vehicleNumber].filter(Boolean).join(' · ');
}

/**
 * Cash the platform believes is in this rider's pocket.
 *
 * `collected` minus `handedOver` since the last settlement. Sums are the
 * server's, not this app's: a rider and the office disagreeing about a total
 * is the one arithmetic error in this product that ends in an argument.
 */
export type RiderCashBalance = {
  collected: Money;
  handedOver: Money;
  /** What is still owed to the office. */
  outstanding: Money;
  /** Number of COD orders behind `outstanding`. */
  entries: number;
  since: string | null;
};

// ── deliveries ──────────────────────────────────────────────────────────────

/**
 * The rider's journey for one fulfilment leg, mirroring `deliveryMachine`.
 *
 * Kept as a union rather than a loose string because every screen in this app
 * switches on it — which action to offer, whether the address is visible,
 * whether the job is still live. A string comparison scattered across six
 * files is where the two halves quietly stop agreeing.
 */
export type DeliveryStatus =
  | 'PENDING'
  | 'ASSIGNED'
  | 'ACCEPTED'
  | 'ARRIVED_AT_PHARMACY'
  | 'PICKED_UP'
  | 'OUT_FOR_DELIVERY'
  | 'ARRIVED_AT_CUSTOMER'
  | 'DELIVERED'
  | 'FAILED'
  | 'CANCELLED';

/**
 * Where the pharmacy has got to on its half.
 *
 * Mirrors `order_pharmacy_status` exactly, including the two states that look
 * like one — `PACKED` is the shop having finished filling the bag, and
 * `READY_FOR_PICKUP` is it being on the counter. Only the second one lets a
 * rider collect, which is precisely why both have to be modelled: a run sheet
 * that treated "packed" as collectable would send someone to a counter with
 * nothing on it and then fail the pickup with a 409.
 */
export type LegStatus =
  | 'PENDING'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'PICKING'
  | 'PACKED'
  | 'READY_FOR_PICKUP'
  | 'HANDED_OVER'
  | 'CANCELLED';

/**
 * How far one leg is — an ESTIMATE from the shop's pin and the customer's.
 *
 * The server takes the straight line, allows for streets not being straight,
 * and times it at an average city riding speed. Good for judging the trip at a
 * glance, which is all it is for: the real route comes from Google Maps
 * through the deeplink. Always shown with "about".
 */
export type RouteLeg = {
  distanceMeters: number;
  durationSeconds: number;
  source: 'ESTIMATE';
};

/** One row on the run sheet. */
export type DeliveryJob = {
  id: string;
  status: DeliveryStatus;
  /**
   * When the job was opened, which is when the pharmacy accepted the order —
   * not when it was handed to this rider. The age a dispatcher is watching.
   */
  createdAt: string;

  orderNumber?: string | null;
  orderStatus?: string | null;

  /**
   * The shop, by id rather than by name.
   *
   * Grouping a run sheet on the NAME merges two genuinely different branches
   * that share a trading name, and splits one shop the day somebody corrects
   * its spelling. Three bags at one counter is one stop, and this is what says
   * so.
   */
  pharmacyId?: string | null;
  pharmacyName?: string | null;
  pharmacyPhone?: string | null;
  pharmacyLatitude?: string | null;
  pharmacyLongitude?: string | null;

  /**
   * Where the drop is, on the LIST.
   *
   * Null until the medicine is in this rider's hands — the backend applies the
   * same withholding rule here as on a single delivery, so a run sheet cannot
   * become the way around it. Present only so the sheet can order the drops by
   * distance without a request per job.
   */
  dropLatitude?: string | null;
  dropLongitude?: string | null;

  customerName?: string | null;

  /**
   * The leg itself: pharmacy counter to customer door, measured by the server.
   *
   * Sent from the moment the job is assigned, unlike the address — a distance
   * names no street, and it is what a rider needs to judge the trip. Null when
   * either end has no map pin.
   */
  route?: RouteLeg | null;

  paymentMethod?: string | null;
  paymentStatus?: string | null;
  /**
   * The order's grand total. Only actually collectable when the order is COD
   * and unpaid; on a prepaid order the same number is on the bill and nothing
   * is owed. Use `isCashOnDelivery` rather than reading this directly.
   */
  codAmount?: Money | null;

  /**
   * Pickup is refused until this reads `READY_FOR_PICKUP`, so the run sheet
   * shows it rather than letting a rider ride over to an empty counter.
   */
  legStatus?: LegStatus | null;

  assignedAt?: string | null;
  acceptedAt?: string | null;
  arrivedAtPharmacyAt?: string | null;
  pickedUpAt?: string | null;
  arrivedAtCustomerAt?: string | null;
  deliveredAt?: string | null;
  failureReason?: string | null;
};

/**
 * Where the order is going.
 *
 * Null on the wire until the medicine is in the rider's hands — the backend
 * withholds it, and this app must not pretend otherwise. A rider who may still
 * be reassigned has no reason to know where a patient lives.
 */
export type DeliveryAddress = {
  line1?: string | null;
  line2?: string | null;
  landmark?: string | null;
  city?: string | null;
  pincode?: string | null;
  /** Decimal strings, like money — parsed only to hand to a maps link. */
  latitude?: string | null;
  longitude?: string | null;
};

/**
 * One job, open on screen.
 *
 * Everything a `DeliveryJob` has, plus the parts the backend only sends for a
 * single delivery: the pharmacy's address, and — once the bag is in hand — the
 * customer's phone number and door.
 */
export type DeliveryDetail = DeliveryJob & {
  orderId?: string | null;
  pharmacyId?: string | null;
  pharmacyAddress?: string | null;
  pharmacyLatitude?: string | null;
  pharmacyLongitude?: string | null;

  /** Withheld until pickup, exactly like `deliveryAddress`. */
  customerPhone?: string | null;
  deliveryAddress?: DeliveryAddress | null;

  /** Root-relative from the local storage driver; made absolute on the way in. */
  proofOfDeliveryUrl?: string | null;
};

/**
 * What `POST /deliveries/:id/pickup` answers.
 *
 * The handover code sits BESIDE `data` rather than inside it because it is
 * minted once and never readable again — only its hash is stored. Modelling it
 * as a field of the delivery would imply a later fetch could return it.
 */
export type PickupResult = {
  delivery: DeliveryDetail;
  customerOtp: string | null;
};

export type StoredFile = { key: string; url?: string | null };

/**
 * What `POST /deliveries/pickup` answers: several bags off one counter.
 *
 * Deliberately not all-or-nothing. One bag out of three not yet packed is the
 * normal case, so each delivery gets its own answer — the rider is told which
 * bags they now hold, and why the rest were left on the counter.
 */
export type BatchPickupResult = {
  collected: Array<{ deliveryId: string; orderNumber: string | null; customerOtp: string }>;
  skipped: Array<{ deliveryId: string; code: string; message: string }>;
};
